// 094 -- Admin Orders dashboard. Super-admin only.

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { AdminOrderFilters, OrderPublic, OrderStatus, ProviderPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Breadcrumbs } from "@/components/breadcrumbs";

function fmtMoney(cents: number, currency: string): string {
  return `${currency === "ZAR" ? "R " : `${currency} `}${(cents / 100).toFixed(2)}`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
}

const STATUS_BADGE: Record<OrderStatus, string> = {
  submitted: "bg-yellow-100 text-yellow-800",
  acknowledged: "bg-blue-100 text-blue-800",
  fulfilled: "bg-green-100 text-green-800",
  cancelled: "bg-muted text-muted-foreground",
  failed: "bg-red-100 text-red-800",
};

const STATUS_OPTIONS: OrderStatus[] = ["submitted", "acknowledged", "fulfilled", "cancelled", "failed"];

export default function AdminShopOrdersPage() {
  const location = useLocation();
  const [orders, setOrders] = useState<OrderPublic[]>([]);
  const [providers, setProviders] = useState<ProviderPublic[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState<AdminOrderFilters>({});

  useEffect(() => {
    api.getAdminProviders().then((r) => setProviders(r.providers)).catch(() => { /* ignore */ });
  }, []);

  useEffect(() => {
    setLoading(true);
    api.getAdminOrders({ ...filters, page, page_size: 25 })
      .then((r) => {
        setOrders(r.orders);
        setTotal(r.total);
        setError(null);
      })
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [location.key, filters, page]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / 25)), [total]);

  return (
    <div
      data-page="admin-shop-orders"
      data-state={loading ? "loading" : "ready"}
      className="flex flex-col gap-6"
    >
      <Breadcrumbs items={[{ label: "Admin" }, { label: "Shop orders" }]} />
      <div>
        <h1 className="text-2xl font-semibold">Shop orders</h1>
        <p className="text-sm text-muted-foreground">All orders across providers. Use the filters to narrow down.</p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="f-provider" className="text-xs text-muted-foreground">Provider</label>
            <select
              id="f-provider"
              data-field="provider_id"
              value={filters.provider_id ?? ""}
              onChange={(e) => { setPage(1); setFilters({ ...filters, provider_id: e.target.value || undefined }); }}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              aria-label="Provider filter"
            >
              <option value="">Any provider</option>
              {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="f-status" className="text-xs text-muted-foreground">Status</label>
            <select
              id="f-status"
              data-field="status"
              value={filters.status ?? ""}
              onChange={(e) => { setPage(1); setFilters({ ...filters, status: (e.target.value || undefined) as OrderStatus | undefined }); }}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              aria-label="Status filter"
            >
              <option value="">Any status</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="f-buyer" className="text-xs text-muted-foreground">Buyer</label>
            <select
              id="f-buyer"
              data-field="buyer_type"
              value={filters.buyer_type ?? ""}
              onChange={(e) => { setPage(1); setFilters({ ...filters, buyer_type: (e.target.value || undefined) as "parent" | "school" | undefined }); }}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              aria-label="Buyer type filter"
            >
              <option value="">Any buyer</option>
              <option value="parent">Parent</option>
              <option value="school">School</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="f-from" className="text-xs text-muted-foreground">From</label>
            <Input
              id="f-from"
              data-field="from_date"
              type="date"
              value={filters.from_date ?? ""}
              onChange={(e) => { setPage(1); setFilters({ ...filters, from_date: e.target.value || undefined }); }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="f-to" className="text-xs text-muted-foreground">To</label>
            <Input
              id="f-to"
              data-field="to_date"
              type="date"
              value={filters.to_date ?? ""}
              onChange={(e) => { setPage(1); setFilters({ ...filters, to_date: e.target.value || undefined }); }}
            />
          </div>
          {(filters.provider_id || filters.status || filters.buyer_type || filters.from_date || filters.to_date) && (
            <Button variant="ghost" size="sm" onClick={() => { setPage(1); setFilters({}); }} data-action="clear-filters">
              Clear
            </Button>
          )}
        </CardContent>
      </Card>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {loading ? (
        <p className="text-muted-foreground" data-state="loading">Loading…</p>
      ) : orders.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">No orders match the current filters.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm" data-region="orders">
              <thead>
                <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                  <th className="py-2 px-4 text-left">Order</th>
                  <th className="py-2 px-4 text-left">Provider</th>
                  <th className="py-2 px-4 text-left">Buyer</th>
                  <th className="py-2 px-4 text-left">Status</th>
                  <th className="py-2 px-4 text-left">Date</th>
                  <th className="py-2 px-4 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-border/60 hover:bg-muted/30" data-item="order" data-item-id={o.id}>
                    <td className="py-3 px-4">
                      <Link to={`/admin/shop/orders/${o.slug}`} className="font-medium text-primary hover:underline">#{o.slug}</Link>
                    </td>
                    <td className="py-3 px-4">{o.provider.name}</td>
                    <td className="py-3 px-4">
                      {o.buyer_type === "school" ? (o.school_name ?? "School") : "Parent"}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs uppercase ${STATUS_BADGE[o.status]}`}>
                        {o.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{fmtDate(o.submitted_at)}</td>
                    <td className="py-3 px-4 text-right font-medium tabular-nums">{fmtMoney(o.total_cents, o.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{total} total</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} data-action="prev">Prev</Button>
          <span>{page} / {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} data-action="next">Next</Button>
        </div>
      </div>
    </div>
  );
}
